/**
 * IAST Pronunciation Guide — canonical content.
 *
 * Single source of truth. Copied verbatim into each client app:
 *   - jnana-native/src/components/pronunciationGuide/iastGuideContent.ts
 *   - jnana-web/src/pronunciation/iastGuideContent.ts
 *
 * No imports so it stays portable. Every example word (iast + devanagari) is
 * verified present in the real content dumps (jnana-scripts/large-responses/
 * en.json + hi.json, joined by nodeId). Do not invent spellings — verify first.
 *
 * Reviewed for linguistic accuracy (Sanskrit/IAST phonetics) and UX (kids +
 * older adults). See docs/superpowers/specs/2026-07-04-iast-pronunciation-guide-design.md
 */

export interface ExampleWord {
  /** IAST (roman with diacritics) — primary, large. */
  iast: string;
  /** Devanagari — secondary, muted; a reliable anchor for those who read it. */
  devanagari: string;
}

export interface RuleCard {
  id: string;
  /** The mark or letter(s) this card is about, e.g. "ā ī ū ē ō". */
  markLabel: string;
  /** Short warm title, e.g. "The long mark". */
  title: string;
  /** One-line rule in plain, warm language. */
  rule: string;
  /** Optional honest caveat shown small under the rule. */
  note?: string;
  examples: ExampleWord[];
}

export interface SurpriseItem {
  /** The letter(s), e.g. "v" or "ph  th  bh". */
  letters: string;
  /** One-line explanation. */
  text: string;
}

export interface ReferenceRow {
  letters: string;
  sound: string;
}

export interface ReferenceGroup {
  label: string;
  rows: ReferenceRow[];
}

export interface IastGuideContent {
  title: string;
  /** Two-sentence "big idea". */
  bigIdea: string[];
  /** "Start here" — the two highest-payoff truths, before the cards. */
  startHere: { heading: string; body: string; example?: ExampleWord }[];
  cards: RuleCard[];
  surprisesHeading: string;
  surprises: SurpriseItem[];
  reference: {
    heading: string;
    vowels: ReferenceRow[];
    consonants: ReferenceGroup[];
    footnote: string;
  };
}

export const IAST_GUIDE: IastGuideContent = {
  title: 'How to Read the Words',

  bigIdea: [
    'Reading these words is easier than it looks. They use the same letters you already know — plus a few small marks.',
    'Each mark is just one simple rule. Learn a handful and you can read anything.',
  ],

  startHere: [
    {
      heading: 'A plain "a" is a soft "uh"',
      body: 'Like the "u" in "but" or the "a" in "sofa" — not the "a" in "cat".',
      example: { iast: 'namaḥ', devanagari: 'नमः' }, // ≈ "nuh-muh"
    },
    {
      heading: 'A line on top = hold it longer',
      body: 'ā = "aa" (father), ī = "ee" (see), ū = "oo" (blue), ē = "ay" (play), ō = "o" (go).',
      example: { iast: 'rāma', devanagari: 'राम' },
    },
  ],

  cards: [
    {
      id: 'long-mark',
      markLabel: 'ā  ī  ū  ē  ō',
      title: 'The long mark',
      rule: 'A line on top means hold the vowel a little longer and clearer. ā sounds like the "aa" in "father".',
      examples: [
        { iast: 'rāma', devanagari: 'राम' },
        { iast: 'śrī', devanagari: 'श्री' },
        { iast: 'gōvindā', devanagari: 'गोविंदा' },
        { iast: 'ōṁ', devanagari: 'ओं' },
      ],
    },
    {
      id: 'sha',
      markLabel: 'ś  ṣ',
      title: 'Both say "sh"',
      rule: 'ś is the "sh" in "ship" (tongue forward). ṣ is a deeper "sh" with the tongue curled back.',
      examples: [
        { iast: 'śrī', devanagari: 'श्री' },
        { iast: 'viṣṇu', devanagari: 'विष्णु' },
        { iast: 'lakṣmī', devanagari: 'लक्ष्मी' },
      ],
    },
    {
      id: 'ca',
      markLabel: 'c',
      title: '"c" is always "ch"',
      rule: 'c sounds like the "ch" in "church" — never a "k" or an "s". (ch is the same sound, with a puff of air.)',
      examples: [
        { iast: 'caraṇaṁ', devanagari: 'चरणं' },
        { iast: 'candra', devanagari: 'चंद्र' },
        { iast: 'vacana', devanagari: 'वचन' },
      ],
    },
    {
      id: 'retroflex',
      markLabel: 'ṭ  ḍ  ṇ',
      title: 'A dot underneath',
      rule: 'Curl your tongue tip up and back to the roof of your mouth, then say t, d, or n — a fuller, rounder sound.',
      note: 'Without the dot, t/d/n are softer, with the tongue near your teeth.',
      examples: [
        { iast: 'gaṇapati', devanagari: 'गणपति' },
        { iast: 'kōṭi', devanagari: 'कोटि' },
        { iast: 'ḍōlā', devanagari: 'डोला' },
      ],
    },
    {
      id: 'nasals',
      markLabel: 'ṅ  ñ  ṁ',
      title: 'Nose sounds',
      rule: 'ṅ = "ng" (sing). ñ = "ny" (canyon). ṁ = a soft nasal hum, usually "m" at the end of a word.',
      note: 'In "jñāna", the "jñ" is often said "gy" ("gyaana") — a common regional twist. Inside a word, ṁ blends into the next sound.',
      examples: [
        { iast: 'gaṅgā', devanagari: 'गंगा' },
        { iast: 'jñāna', devanagari: 'ज्ञान' },
        { iast: 'ōṁ', devanagari: 'ओं' },
        { iast: 'māṁ', devanagari: 'मां' },
      ],
    },
    {
      id: 'visarga',
      markLabel: 'ḥ',
      title: 'The breath mark',
      rule: 'A soft breath after the vowel — a quiet echo, not a whole new syllable. "namaḥ" sounds like "nama(h)".',
      examples: [
        { iast: 'namaḥ', devanagari: 'नमः' },
        { iast: 'mātaḥ', devanagari: 'मातः' },
        { iast: 'yōgaḥ', devanagari: 'योगः' },
      ],
    },
  ],

  surprisesHeading: 'A few letters that surprise you',
  surprises: [
    { letters: 'v', text: 'A soft "v", halfway to a "w" (vandē, dēva).' },
    { letters: 'r', text: 'A light tapped or rolled "r" — the tongue tip flicks — not the heavy English "r" (rāma, hari).' },
    {
      letters: 'kh gh ch jh ṭh ḍh th dh ph bh',
      text: 'Each pair is ONE sound: the plain letter plus a small puff of breath. So ph = p + puff (not "f"), th = t + puff (not the "th" in "the"), and bh = b + puff — that\'s the "bh" in "bhajan".',
    },
  ],

  reference: {
    heading: 'See the full chart',
    vowels: [
      { letters: 'a', sound: '"uh" (but)' },
      { letters: 'ā', sound: '"aa" (father)' },
      { letters: 'i', sound: '"i" (sit)' },
      { letters: 'ī', sound: '"ee" (see)' },
      { letters: 'u', sound: '"u" (put)' },
      { letters: 'ū', sound: '"oo" (blue)' },
      { letters: 'ṛ', sound: '"ri" (Kṛṣṇa)' },
      { letters: 'e / ē', sound: '"ay" (play)' },
      { letters: 'ai', sound: '"eye" (aisle)' },
      { letters: 'o / ō', sound: '"o" (go)' },
      { letters: 'au', sound: '"ow" (cow)' },
    ],
    consonants: [
      {
        label: 'Throat (velar)',
        rows: [
          { letters: 'k  kh', sound: 'k / k + puff' },
          { letters: 'g  gh', sound: 'g / g + puff' },
          { letters: 'ṅ', sound: '"ng" (sing)' },
        ],
      },
      {
        label: 'Palate (palatal)',
        rows: [
          { letters: 'c  ch', sound: '"ch" (church) / + puff' },
          { letters: 'j  jh', sound: '"j" (jump) / + puff' },
          { letters: 'ñ', sound: '"ny" (canyon)' },
        ],
      },
      {
        label: 'Curled tongue (retroflex)',
        rows: [
          { letters: 'ṭ  ṭh', sound: 't / t + puff — tongue curled back' },
          { letters: 'ḍ  ḍh', sound: 'd / d + puff — tongue curled back' },
          { letters: 'ṇ', sound: 'n — tongue curled back' },
        ],
      },
      {
        label: 'Teeth (dental)',
        rows: [
          { letters: 't  th', sound: 't / t + puff — tongue on teeth' },
          { letters: 'd  dh', sound: 'd / d + puff — tongue on teeth' },
          { letters: 'n', sound: 'n — tongue on teeth' },
        ],
      },
      {
        label: 'Lips',
        rows: [
          { letters: 'p  ph', sound: 'p / p + puff (not "f")' },
          { letters: 'b  bh', sound: 'b / b + puff (bhajan)' },
          { letters: 'm', sound: 'm' },
        ],
      },
      {
        label: 'Others',
        rows: [
          { letters: 'y', sound: '"y" (yes)' },
          { letters: 'r', sound: 'a light tapped "r"' },
          { letters: 'l', sound: 'l' },
          { letters: 'v', sound: 'soft "v", toward "w"' },
          { letters: 'ś  ṣ', sound: 'both "sh"' },
          { letters: 's', sound: 's' },
          { letters: 'h', sound: 'h' },
        ],
      },
    ],
    footnote:
      'ṛ ("ri") and ṃ are rare in these songs but appear in wider Sanskrit texts, so they are listed here for completeness.',
  },
};
